import { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, Link2, Share2, Info } from 'lucide-react';
import { useAccess } from '@/components/dashboard/access';

// Explicación neutra para Access y PRO (referidores): 10 % plano del primer pago, nunca renovaciones ni upgrades
const REFERRER_NOTE = 'Si alguien se hace miembro con tu enlace, recibes un 10 % de su primer pago.';

// Dominio público desde el que se generan los enlaces de referido.
// Si en el futuro va por env, importar de @/config/api.
const PUBLIC_BASE = 'https://freedomhomes.club';

/**
 * Catálogo de enlaces compartibles. Añadir aquí uno nuevo y aparece en
 * Home, Perfil y Marketing automáticamente.
 *
 * El orden importa: el primero es el más destacado.
 *   - shortLabel → pill que aparece en variant="compact" (Home)
 *   - title      → header de la card en variant="full" (Perfil, Marketing)
 *   - description→ texto explicativo en variant="full"
 */
const buildLinks = (refCode) => [
  {
    key: 'register',
    shortLabel: 'Registro',
    title: 'Registro directo en la plataforma',
    description: 'Lleva al lead directo al formulario de registro. El registro queda asociado a tu código.',
    url: `${PUBLIC_BASE}/dashboard/register?ref=${refCode}`,
  },
  {
    key: 'main',
    shortLabel: 'Home',
    title: 'Tu enlace de referido',
    description: 'Enlace a la home pública. Quien se registre desde él queda vinculado a tu código.',
    url: `${PUBLIC_BASE}/?ref=${refCode}`,
  },
  {
    // Sustituye al enlace de eventos (Samuel, 02/11/2026): la próxima masterclass en directo, con registro de solo nombre y email
    key: 'masterclass',
    shortLabel: 'Masterclass',
    title: 'Masterclass en directo',
    description: 'Registro a la próxima masterclass online, solo con nombre y email. Quien se apunte desde tu enlace queda asociado a tu código.',
    url: `${PUBLIC_BASE}/masterclass-en-directo?ref=${refCode}`,
  },
  {
    key: 'presentacion',
    shortLabel: 'Presentación',
    title: 'Video de presentación',
    description: 'Landing con video de presentación de la oportunidad. Ideal para enviar a contactos que aún no conocen FHC — formulario de contacto integrado.',
    url: `${PUBLIC_BASE}/presentacion?ref=${refCode}`,
  },
];

function CopyRow({ url, onCopy, copied }) {
  return (
    <div className="flex items-center gap-2">
      <input
        readOnly
        value={url}
        onClick={(e) => e.target.select()}
        className="flex-1 min-w-0 bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-2.5 text-sm text-gray-400 focus:outline-none focus:border-[#c9a84c]/30 transition-colors"
      />
      <button
        onClick={onCopy}
        className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] font-semibold transition-colors shadow-lg shadow-[#c9a84c]/15 shrink-0"
      >
        {copied ? (
          <>
            <Check size={15} /> Copiado
          </>
        ) : (
          <>
            <Copy size={15} /> Copiar
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Fila compacta de la variant="compact" — pill-label + URL + botón copia,
 * todo en una línea. Pensada para apilar varias dentro de una misma card.
 */
function CompactRow({ link, copied, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center justify-center min-w-[64px] px-2 py-2.5 rounded-xl text-[11px] uppercase tracking-wider font-semibold bg-[#c9a84c]/[0.1] text-[#c9a84c] border border-[#c9a84c]/20 shrink-0">
        {link.shortLabel}
      </span>
      <input
        readOnly
        value={link.url}
        onClick={(e) => e.target.select()}
        className="flex-1 min-w-0 bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 py-2.5 text-xs text-gray-400 focus:outline-none focus:border-[#c9a84c]/30 transition-colors"
      />
      <button
        onClick={onCopy}
        className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] transition-colors shadow-lg shadow-[#c9a84c]/15 shrink-0"
        title={copied ? 'Copiado' : 'Copiar'}
        aria-label={copied ? 'Copiado' : `Copiar enlace ${link.shortLabel}`}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
    </div>
  );
}

/**
 * Card reutilizable con los enlaces de afiliado del usuario.
 *
 * Props:
 *   refCode      — código de referido del usuario (p.referral_code)
 *   variant      — 'full' (default, cada link en su propia card con descripción)
 *                | 'compact' (todos los links apilados en una sola card, pensada para Home)
 *   delay        — animation delay en segundos
 */
export function ReferralLinksCard({ refCode, variant = 'full', delay = 0 }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const access = useAccess();

  // Enlace de invitación: solo miembros de pago (Access, PRO, Premium) y equipo (09/10/2026).
  // La comprobación vive aquí para que ninguna pantalla (Inicio, Perfil, Marketing) se lo muestre a un free.
  if (!refCode || !access.can('referral')) return null;
  const note = access.tier === 'access' || access.tier === 'pro' ? REFERRER_NOTE : null;
  const Note = () => (note ? (
    <p className="flex items-start gap-2 text-xs text-gray-400 leading-relaxed" data-testid="referral-note">
      <Info size={13} className="text-[#c9a84c] mt-0.5 shrink-0" aria-hidden="true" /> {note}
    </p>
  ) : null);

  const links = buildLinks(refCode);

  const handleCopy = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      window.prompt('Copia tu enlace:', url);  // navegadores que no permiten copiar
    }
  };

  // ── Variante compacta (Home) ──
  // Una sola card con los 3 links apilados + 3 botones de copia.
  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay }}
        className="rounded-2xl border border-[#c9a84c]/20 bg-gradient-to-br from-[#c9a84c]/[0.06] to-white/[0.02] p-5"
      >
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#c9a84c]/[0.1] flex items-center justify-center">
              <Share2 size={15} className="text-[#c9a84c]" />
            </div>
            <div>
              <h3 className="text-white font-medium text-sm">Tus enlaces de invitación</h3>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">
                Vinculados a tu código de invitación
              </p>
            </div>
          </div>
          <span className="text-[11px] text-[#c9a84c] uppercase tracking-wider font-semibold break-all sm:whitespace-nowrap sm:mt-1">
            Código: {refCode}
          </span>
        </div>
        {note && <div className="mb-4"><Note /></div>}

        <div className="space-y-2">
          {links.map(link => (
            <CompactRow
              key={link.key}
              link={link}
              copied={copiedKey === link.key}
              onCopy={() => handleCopy(link.key, link.url)}
            />
          ))}
        </div>
      </motion.div>
    );
  }

  // ── Variante full (Perfil, Marketing) ──
  return (
    <div className="space-y-4">
      <Note />
      {links.map((link, i) => (
        <motion.div
          key={link.key}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: delay + Math.min(i, 6) * 0.03 }}
          className="rounded-2xl border border-[#c9a84c]/15 bg-[#c9a84c]/[0.03] p-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#c9a84c]/[0.08] flex items-center justify-center">
              <Link2 size={16} className="text-[#c9a84c]" />
            </div>
            <h3 className="text-white font-medium">{link.title}</h3>
          </div>
          <p className="text-gray-400 text-sm mb-4">{link.description}</p>
          <CopyRow
            url={link.url}
            copied={copiedKey === link.key}
            onCopy={() => handleCopy(link.key, link.url)}
          />
        </motion.div>
      ))}
    </div>
  );
}
